import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, type Video } from '../api/client.ts';
import { PrimaryButton } from '../components/ui/PrimaryButton.tsx';
import { ScreenHeader } from '../components/ui/ScreenHeader.tsx';
import { TextField } from '../components/ui/TextField.tsx';
import { formatDuration, formatSize } from '../lib/format.ts';
import { usePendingUpload } from '../lib/usePendingUpload.ts';
// The form is the upload form with a different verb, so it reuses those styles.
import './UploadScreen.css';
import './EditScreen.css';

/**
 * Admin edit: rename a ride, correct its date, or swap the footage for a
 * different file. Picking a new file starts transferring it at once, exactly as
 * on the upload screen; the file it replaces is deleted once the save lands.
 */
export function EditScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const transfer = usePendingUpload();

  const [video, setVideo] = useState<Video | null>(null);
  const [title, setTitle] = useState('');
  const [tripDate, setTripDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getVideo(id)
      .then((loaded) => {
        if (cancelled) return;
        setVideo(loaded);
        setTitle(loaded.title);
        setTripDate(loaded.tripDate);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Видео не найдено');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleSubmit() {
    if (!canSubmit) return;
    setSaveError(null);
    setSaving(true);
    try {
      await api.updateVideo(id, {
        title: title.trim(),
        tripDate,
        // Omitted unless a replacement finished transferring — the server
        // treats its absence as "keep the file you already have".
        ...(transfer.uploadId
          ? { uploadId: transfer.uploadId, durationSec: transfer.durationSec }
          : {}),
      });
      navigate(`/video/${id}`, { replace: true });
    } catch (err) {
      setSaving(false);
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить изменения');
    }
  }

  // A metadata-only edit needs no transfer at all; a started one must finish.
  const canSubmit =
    video !== null &&
    !saving &&
    !transfer.busy &&
    title.trim().length > 0 &&
    tripDate.length > 0;
  const error = saveError ?? transfer.error;
  const replacing = transfer.file !== null;

  return (
    <div className="screen edit-screen">
      <ScreenHeader
        title="Изменить видео"
        leading={
          <button
            type="button"
            className="icon-button"
            onClick={() => navigate(`/video/${id}`)}
            aria-label="Назад"
          >
            <ChevronLeft size={22} />
          </button>
        }
      />

      <div className="screen-body edit-body">
        {loadError && <div className="form-error">{loadError}</div>}

        {video && (
          <>
            <button
              type="button"
              className="dropzone"
              onClick={() => fileInputRef.current?.click()}
              disabled={transfer.busy || saving}
            >
              <RefreshCw size={26} strokeWidth={1.75} />
              <span className="dropzone__label">
                {replacing ? transfer.file!.name : 'Заменить видеофайл'}
              </span>
              <span className="dropzone__meta">
                {replacing ? (
                  <>
                    {formatSize(transfer.file!.size)}
                    {transfer.durationSec > 0 && ` · ${formatDuration(transfer.durationSec)}`}
                  </>
                ) : (
                  <>
                    Сейчас: {formatSize(video.sizeBytes)}
                    {video.durationSec > 0 && ` · ${formatDuration(video.durationSec)}`}
                  </>
                )}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => {
                setSaveError(null);
                void transfer.pick(e.target.files?.[0]);
              }}
            />

            {transfer.phase !== 'idle' && transfer.phase !== 'probing' && (
              <div className="upload-progress">
                <div className="upload-progress__track">
                  <div
                    className="upload-progress__fill"
                    style={{ width: `${transfer.progress}%` }}
                  />
                </div>
                <div className="upload-progress__label">
                  {transfer.phase === 'uploaded' ? 'Загружено · 100%' : `${transfer.progress}%`}
                </div>
              </div>
            )}

            {transfer.phase === 'probing' && (
              <div className="muted-note upload-status">Читаем видео…</div>
            )}

            {/* Deleting the old footage is not undoable, so say so before it happens. */}
            {transfer.phase === 'uploaded' && (
              <div className="edit-warning">
                После сохранения старый файл будет удалён с сервера.
              </div>
            )}

            {error && <div className="form-error upload-status">{error}</div>}

            <div className="upload-fields">
              <TextField
                placeholder="Название"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={saving}
              />
              <TextField
                type="date"
                placeholder="Дата поездки"
                value={tripDate}
                onChange={(e) => setTripDate(e.target.value)}
                disabled={saving}
              />
              <PrimaryButton className="upload-submit" onClick={handleSubmit} disabled={!canSubmit}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
